import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';
import { manufacturingService } from '../../../services/manufacturingService';
import { supabase } from '../../../lib/supabase';
import { imageUploadService } from '../../../utils/imageUploadService';
import ViewerCanvas from '../../3d-model-viewer/components/ViewerCanvas';
import { Palette, Zap, CheckCircle, ShoppingCart, AlertCircle } from 'lucide-react';

const ComponentDetailModal = ({ component, isOpen, onClose, onAddToCart, onDirectAddToCart, isAuthenticated, onAuthRequired, isGlobalCatalog = false, preview = false }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('3d'); // '3d' | 'image'
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [isQuoting, setIsQuoting] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [mfgQuantity, setMfgQuantity] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quoteError, setQuoteError] = useState('');
  const [allMaterials, setAllMaterials] = useState([]);

  const NO_IMAGE = "/assets/images/no_image.png";

  const modelUrl = React.useMemo(() => {
    if (!component) return null;
    const path = component.stl_file_path || component.stlFilePath || component.gltf_file_path || component.gltfFilePath || component.glbUrl;
    if (path && path !== 'null' && path.trim() !== '') {
      if (path.startsWith('http')) return path;
      const { data } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(path);
      return data?.publicUrl || null;
    }
    // Symmetrical Fallback: If standalone model is not uploaded, load the parent product's model and isolate the component!
    return component.product?.modelUrl || component.product?.gltfFilePath || null;
  }, [component]);

  const staticImages = React.useMemo(() => {
    if (!component) return [];
    const imgs = [];
    if (Array.isArray(component.images)) imgs.push(...component.images);
    if (Array.isArray(component.images_url)) imgs.push(...component.images_url);
    if (Array.isArray(component.globalImages)) imgs.push(...component.globalImages);
    if (component.imageUrl) imgs.push(component.imageUrl);
    if (component.image_url && typeof component.image_url === 'string' && !component.image_url.endsWith('.glb') && !component.image_url.endsWith('.gltf') && !component.image_url.endsWith('.stl')) {
      imgs.push(component.image_url);
    }
    if (component.thumbnailUrl) imgs.push(component.thumbnailUrl);
    if (component.thumbnailPath) imgs.push(component.thumbnailPath);
    if (component.productThumbnail) imgs.push(component.productThumbnail);

    // Resolve public URLs and deduplicate
    const unique = [];
    for (const raw of imgs) {
      if (!raw || typeof raw !== 'string') continue;
      const url = (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('/'))
        ? raw 
        : imageUploadService.getPublicUrl(raw);
      if (url && !unique.includes(url)) {
        unique.push(url);
      }
    }
    return unique;
  }, [component]);

  useEffect(() => {
    if (component) {
      setActiveView(modelUrl ? '3d' : 'image');
      setSelectedImageIndex(0);
      setIsAdded(false);
      setQuoteResult(null);
      setQuoteError('');
      setMfgQuantity(1);
      setSelectedColor('');
      setSelectedMaterial(component.preferred_3d_method || component.mfg3dMaterials?.[0] || '');
    }
  }, [component, modelUrl]);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const mats = await manufacturingService.getMaterials(true);
        setAllMaterials(mats || []);
      } catch (err) {
        console.error('Failed to fetch materials:', err);
      }
    };
    fetchMaterials();
  }, []);

  const handleAddToCartWithQuote = async () => {
    if (!quoteResult || isAdding || isAdded) return;
    
    // Check if colors are available for this material
    const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === selectedMaterial);
    const availableColors = libMat?.colors || [];
    const sellerMap = component.is3dManufacturing ? (component.mfg3dColors || {}) : (component.mfgCncColors || {});
    const sellerOfferedColors = sellerMap[selectedMaterial] || [];
    const filteredColors = availableColors.filter(c => sellerOfferedColors.includes(c));

    if (filteredColors.length > 0 && !selectedColor) {
        setQuoteError("Please select a color before adding to cart.");
        return;
    }
    
    setIsAdding(true);
    try {
      const manufacturingMetadata = {
        type: 'manufacturing',
        quoteId: quoteResult.quote_id,
        supplierName: quoteResult.supplier_name,
        material: selectedMaterial,
        color: selectedColor || 'Standard',
        price: quoteResult.price
      };
      if (onAddToCart) {
        await onAddToCart({ ...component, price: quoteResult.price, metadata: manufacturingMetadata });
        setIsAdded(true);
        setTimeout(() => { 
            setIsAdded(false); 
            onClose(); // SECURE CLOSURE
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleGetQuote = async () => {
    if (!isAuthenticated) { onAuthRequired && onAuthRequired('Please log in to request a 3D print quote.'); return; }
    if (!selectedMaterial) { setQuoteError("Select a material first."); return; }
    
    setIsQuoting(true);
    setQuoteError('');
    try {
      const result = await manufacturingService.getQuote(component.id, mfgQuantity, selectedMaterial);
      if (result.success) setQuoteResult(result);
      else setQuoteError(result.message || 'Failed to get quote');
    } catch (err) {
      setQuoteError(err.message || 'Error occurred');
    } finally {
      setIsQuoting(false);
    }
  };

  const handleViewIn3D = () => {
    const pId = component.productId || component.product?.id;
    if (pId) {
      const cId = component.globalComponentId || component.id;
      const cName = component.name || component.customName || '';
      navigate(`/3d-model-viewer?id=${pId}&componentId=${cId}${cName ? `&componentName=${encodeURIComponent(cName)}` : ''}&viewMode=x-ray-gray&isPlaying=false`);
    } else {
      alert("This component is not linked to a 3D model.");
    }
    onClose();
  };

  if (!isOpen || !component) return null;

  const manufacturerName = (component.is3dManufacturing || component.isCncCapable || component.manufacturerName === 'SELF' || component.manufacturer?.name === 'SELF')
    ? (component.storeName || component.seller?.business_name || 'Direct Seller')
    : (component.manufacturerName || component.manufacturer?.name || 'Generic');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-background/90 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border shadow-2xl rounded-2xl w-full max-w-5xl max-h-[90vh] md:max-h-[85vh] flex flex-col md:flex-row z-10 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
        
        <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors">
          <Icon name="X" size={20} />
        </button>

        {/* Fixed Frame Image Panel: 65% width */}
        <div className="w-full md:w-[65%] bg-muted flex flex-col h-[50vh] md:h-full relative p-4 shrink-0 border-r border-border/10">
          <div className="w-full h-[400px] md:h-[550px] flex items-center justify-center overflow-hidden relative rounded-xl border-2 border-border/50 bg-white shadow-2xl shrink-0">
            {activeView === '3d' && modelUrl ? (
              <div className="w-full h-full relative">
                <ViewerCanvas
                  modelUrl={modelUrl}
                  autoRotate={true}
                  autoRotateSpeed={0.8}
                  isMini={true}
                  selectedComponent={component}
                />
              </div>
            ) : (
              <Image 
                src={staticImages[selectedImageIndex] || NO_IMAGE} 
                alt={component.name} 
                className="max-w-full max-h-full object-contain p-4"
                fallback={NO_IMAGE}
              />
            )}
            
            <div className="absolute top-4 left-4 flex flex-col gap-2">
                {component.condition && (
                    <div className="bg-background/90 text-foreground px-3 py-1.5 rounded-full text-xs font-black shadow-sm backdrop-blur-sm border border-border capitalize">
                        {component.condition}
                    </div>
                )}
                {(component.is3dManufacturing || component.isCncCapable) && (
                  <div className="bg-orange-600 text-white px-3 py-1.5 rounded-full text-[10px] font-black shadow-lg flex items-center gap-1.5 animate-bounce-subtle">
                    <Icon name="Zap" size={12} className="fill-current" />
                    3D PRINT CAPABLE
                  </div>
                )}
            </div>
          </div>
          {((modelUrl ? 1 : 0) + staticImages.length > 1) && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-thin shrink-0 items-center">
              {/* Default 3D Mini-Viewer Representation */}
              {modelUrl && (
                <button
                  type="button"
                  onClick={() => setActiveView('3d')}
                  className={`relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all flex flex-col items-center justify-center bg-gradient-to-br from-card to-muted group ${
                    activeView === '3d'
                      ? 'border-primary ring-2 ring-primary/20 shadow-sm'
                      : 'border-border/60 hover:border-primary/50 opacity-80 hover:opacity-100'
                  }`}
                  title="Default 3D Mini-Viewer"
                >
                  <Icon name="Box" size={20} className={activeView === '3d' ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'} />
                  <span className={`text-[9px] font-black uppercase tracking-tight mt-0.5 ${activeView === '3d' ? 'text-primary' : 'text-muted-foreground'}`}>
                    3D View
                  </span>
                </button>
              )}

              {/* Still Images Representations */}
              {staticImages.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setActiveView('image');
                    setSelectedImageIndex(idx);
                  }}
                  className={`relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all bg-white ${
                    activeView === 'image' && selectedImageIndex === idx
                      ? 'border-primary ring-2 ring-primary/20 shadow-sm'
                      : 'border-border/60 hover:border-primary/50 opacity-80 hover:opacity-100'
                  }`}
                  title={`Still Image ${idx + 1}`}
                >
                  <Image src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" fallback={NO_IMAGE} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: 35% width */}
        <div className="w-full md:w-[35%] p-6 pt-14 md:p-8 md:pt-14 flex flex-col overflow-y-auto relative bg-card h-full">
          <div className="mb-6">
            <h2 className="text-2xl font-black text-foreground tracking-tight leading-tight mb-2 uppercase">{component.customName || component.name}</h2>
            <div className="text-2xl font-black text-primary mb-4 pb-4 border-b border-border">
              {formatPrice(component.price)}
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex justify-between items-center text-[10px] border-b border-border/50 pb-2">
                <span className="text-muted-foreground font-black uppercase tracking-widest">Manufacturer</span>
                <span className="font-black text-foreground">{manufacturerName}</span>
              </div>
              {!(component.is3dManufacturing || component.isCncCapable) && (
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-muted-foreground font-black uppercase tracking-widest">Availability</span>
                  <span className="font-black text-success">In Stock ({component.totalStock || component.total_stock || component.stockQuantity || component.stock_quantity || 0})</span>
                </div>
              )}
            </div>

            {/* 3D Manufacturing - Filtered to Seller Selection */}
            {(component.is3dManufacturing || component.isCncCapable) && (
              <div className="bg-blue-600 border border-blue-500 rounded-xl p-5 mb-6 shadow-xl shadow-blue-100">
                <h3 className="text-xs font-black text-white flex items-center gap-2 mb-4">
                  <Zap className="w-4 h-4 text-yellow-400 fill-current" />
                  3D PRINTING SERVICE
                </h3>
                {!quoteResult ? (
                  <div className="space-y-4">
                    <select 
                      value={selectedMaterial}
                      onChange={(e) => { setSelectedMaterial(e.target.value); setSelectedColor(''); }}
                      className="w-full text-xs font-black p-3 rounded-lg border-none bg-white text-blue-900 focus:ring-2 focus:ring-blue-400 outline-none shadow-inner cursor-pointer appearance-none"
                    >
                      <option value="">Select material...</option>
                      {[...new Set([...(component.mfg3dMaterials || []), ...(component.mfgCncMaterials || [])])].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>

                    {selectedMaterial && (
                      <div className="grid grid-cols-2 gap-2">
                         {(() => {
                           const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === selectedMaterial);
                           const availableColors = libMat?.colors || [];
                           const sellerMap = component.is3dManufacturing ? (component.mfg3dColors || {}) : (component.mfgCncColors || {});
                           const sellerColors = sellerMap[selectedMaterial] || [];

                           const filteredColors = availableColors.filter(c => sellerColors.includes(c));

                           if (filteredColors.length === 0) return <p className="text-[10px] text-blue-100 font-bold italic col-span-full py-2 text-center bg-blue-700/50 rounded-lg border border-blue-400/30">Standard finish only</p>;

                           return filteredColors.map(color => (
                             <button
                               key={color}
                               type="button"
                               onClick={() => setSelectedColor(color)}
                               className={`px-2 py-2 rounded-lg text-[10px] font-black border-2 transition-all flex items-center justify-between ${
                                 selectedColor === color ? 'bg-yellow-400 text-blue-900 border-yellow-300 shadow-md scale-[1.02]' : 'bg-blue-700/50 text-white border-blue-400 hover:bg-blue-700'
                               }`}
                             >
                               <span className="truncate">{color}</span>
                               <CheckCircle className={`w-3.5 h-3.5 shrink-0 ${selectedColor === color ? 'opacity-100 text-blue-900' : 'opacity-0'}`} />
                             </button>
                           ));
                         })()}
                      </div>
                    )}

                    <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-black h-10 text-[10px] uppercase tracking-widest shadow-lg border-b-2 border-yellow-600" onClick={handleGetQuote} disabled={isQuoting || !selectedMaterial}>
                       {isQuoting ? 'CALCULATING...' : 'GET INSTANT QUOTE'}
                    </Button>
                    {quoteError && <div className="flex items-center gap-1.5 text-red-100 text-[10px] font-bold bg-red-900/40 p-2 rounded-lg border border-red-400/20 animate-in fade-in"><AlertCircle size={12}/> {quoteError}</div>}
                  </div>
                ) : (
                  <div className="space-y-4 animate-in zoom-in-95">
                    <div className="bg-white p-4 rounded-xl flex justify-between items-center shadow-inner">
                        <div>
                            <p className="text-[9px] text-blue-600 font-black uppercase tracking-tighter">Production Price</p>
                            <p className="text-2xl font-black text-blue-900 leading-none">{formatPrice(quoteResult.price)}</p>
                        </div>
                        <button onClick={() => setQuoteResult(null)} className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-1.5 rounded-lg border border-blue-100">CHANGE</button>
                    </div>
                    <Button className="w-full bg-green-500 hover:bg-green-600 text-white font-black h-12 text-xs uppercase tracking-widest border-b-4 border-green-700 active:border-b-0 active:translate-y-1 transition-all" onClick={handleAddToCartWithQuote} disabled={isAdding}>
                        {isAdding ? 'ADDING...' : 'ADD TO CART'}
                    </Button>
                    {quoteError && <div className="text-red-100 text-[10px] font-bold text-center">{quoteError}</div>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-6 mb-8">
               <div>
                  <h3 className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-2 border-b border-border/50 pb-1">Description</h3>
                  <p className="text-xs leading-relaxed text-foreground/80 font-medium">
                    {component?.description || 'No description available for this component.'}
                  </p>
               </div>
               
               {(() => {
                 const IGNORED_SPEC_KEYS = [
                   'meshUuid', 'uuid', 'type', 'children', 'material', 'matrix', 'castShadow', 
                   'receiveShadow', 'visible', 'up', 'quaternion', 'userData', 'position', 
                   'rotation', 'scale', 'normal', 'uv', 'index', 'groups', 'drawRange', 'geometry',
                   'vertices', 'faces', 'attributes', 'drawrange', 'isMesh', 'isGroup'
                 ];

                 const filteredSpecs = Object.entries(component?.specifications || {}).filter(([key, value]) => {
                   return (
                     !IGNORED_SPEC_KEYS.includes(key) && 
                     !key.startsWith('_') && 
                     typeof value !== 'object' &&
                     value !== null &&
                     value !== ''
                   );
                 });

                 if (filteredSpecs.length === 0) return null;

                 return (
                   <div>
                      <h3 className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-2 border-b border-border/50 pb-1">Technical Specs</h3>
                      <div className="space-y-1.5">
                        {filteredSpecs.map(([key, value]) => (
                          <div key={key} className="text-[11px] flex justify-between gap-4">
                              <span className="font-bold text-muted-foreground uppercase tracking-tight">{key.replace(/_/g, ' ')}</span>
                              <span className="text-foreground text-right font-black truncate">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                   </div>
                 );
               })()}
          </div>

          <div className="mt-auto space-y-3 pt-6 border-t border-border">
            <Button variant="default" size="lg" className="w-full text-xs font-black uppercase tracking-widest shadow-xl border-b-4 border-primary/50" iconName="ShoppingCart" onClick={() => !isAuthenticated ? onAuthRequired('Please log in') : onAddToCart(component)}>
              {isAdding ? 'Adding...' : 'Add to Cart'}
            </Button>
            <Button variant="secondary" size="lg" className="w-full text-xs font-black uppercase tracking-widest shadow-sm" iconName="Box" onClick={handleViewIn3D}>
              View in 3D Model
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentDetailModal;
